import { Box, Button, FormControl } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import DataTable, { DataTableFilter, DataTableFilterItem } from "../components/DataTable";
import Api from "../openapi";
import { DeliveryReviewResponse, ListReviewsByDateStatusEnum, PausedSubscriptionResponse } from "../openapi/client";
import { GridPaginationModel, GridCallbackDetails, GridSortModel, GridRenderCellParams } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { defaultFormat, displayFormat, generateRandom } from "../utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import useDietitians from "../hooks/useDietitians";
import useDrivers from "../hooks/useDrivers";
import { getUserNameWithIcon } from "./Users";
import ReviewDialog from "../components/ReviewDialog";
import useMealTypes from "../hooks/useMealTypes";

export default function Review() {
  const [reviewDialog, setReviewDialog] = useState({ open: false, userId: "", date: "" });
  const [searchParams, setSearchParams] = useSearchParams();
  const mealTypeList = useMealTypes();
  const [selectedDate, setSelectedDate] = useState(
    searchParams.get("date") ? moment(searchParams.get("date")) : moment()
  );
  const [activeTab, setActiveTab] = useState(0);
  const [packages, setPackages] = useState<any[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [retriggerReq, setRetriggerReq] = useState<number>();

  const drivers = useDrivers();
  const dietitians = useDietitians();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: "user.fullName",
      sort: "asc",
    },
  ]);

  const [filterModel, setFilterModel] = useState<DataTableFilter>({
    userExternalId: null,
    userName: null,
    status: null,
    driverId: null,
    dietitianId: null,
  });

  // Paused tab state
  const [pausedSubscriptions, setPausedSubscriptions] = useState<any[]>([]);
  const [pausedTotalRowCount, setPausedTotalRowCount] = useState(0);
  const [pausedLoading, setPausedLoading] = useState(false);
  const [pausedPaginationModel, setPausedPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [pausedSortModel, setPausedSortModel] = useState<GridSortModel>([
    {
      field: "fullName",
      sort: "asc",
    },
  ]);

  const filterItems: DataTableFilterItem[] = [
    {
      field: "userExternalId",
      label: "User External Id",
      type: "text",
    },
    {
      field: "userName",
      label: "Member Name",
      type: "text",
    },
    {
      field: "status",
      label: "Status",
      type: "select",
      listData: [
        { id: ListReviewsByDateStatusEnum.PendingReview, fullName: "Pending Review" },
        { id: ListReviewsByDateStatusEnum.Reviewed, fullName: "Reviewed" },
      ],
    },
    {
      field: "dietitianId",
      label: "Dietitian",
      type: "select",
      listData: dietitians,
    },
    {
      field: "driverId",
      label: "Driver",
      type: "select",
      listData: drivers,
    },
  ];

  const handlePaginationChange = (model: GridPaginationModel, details: GridCallbackDetails<any>) => {
    setPaginationModel(model);
  };
  const handleSortChange = (sortModel: GridSortModel) => {
    sortModel.length && setSortModel(sortModel);
  };

  const handlePausedPaginationChange = (model: GridPaginationModel, details: GridCallbackDetails<any>) => {
    setPausedPaginationModel(model);
  };
  const handlePausedSortChange = (sortModel: GridSortModel) => {
    sortModel.length && setPausedSortModel(sortModel);
  };

  const updateActiveTab = (index: number) => {
    setActiveTab(index);
  };

  const toAddressString = (address: any) => {
    var str = [];

    if (address.area) {
      str.push("AREA " + address.area);
    }
    if (address.zone) {
      str.push("ZONE " + address.zone);
    }
    if (address.street) {
      str.push("ST. " + address.street);
    }

    if (address.building) {
      str.push("BN. " + address.building);
    }

    if (address.flat) {
      str.push("FL. " + address.flat);
    }

    return str.join(", ");
  };

  const columns = useMemo(() => {
    return [
      {
        field: "user.externalId",
        minWidth: 110,
        flex: 0,
        headerName: "Member ID",
        valueGetter: (params: any) => params.row.user.externalId,
      },
      {
        field: "user.fullName",
        minWidth: 120,
        flex: 2,
        headerName: "Name",
        renderCell: (params: any) => getUserNameWithIcon(params.row.user.fullName, params.row.user.channel),
      },
      {
        field: "user.membership.phoneNumber",
        minWidth: 130,
        flex: 0,
        headerName: "Phone",
        valueGetter: (params: any) => params.row.user.phoneNumber,
      },
      {
        field: "subscription.planOption.plan.name,subscription.planOption.selection",
        minWidth: 200,
        sortable: true,
        flex: 1.5,
        headerName: "Subscription",
        renderCell: (params: any) => (
          <Box>
            <Box fontWeight={600}>{params.row.subscription?.planName + " - " + params.row.subscription?.planOption?.selection}</Box>
            <Box fontSize={10}>
              {params.row.subscription?.planOption?.mealTypes?.split(",").map((i: string) => mealTypeList.find((li) => li.name === i)?.description).join(", ")}
            </Box>
          </Box>
        )
      },
      {
        field: "status",
        minWidth: 180,
        flex: 0,
        headerName: "Status",
        renderCell: (params: any) => (
          <Box className={`status-label ${params.row.status === "PENDING_REVIEW" ? "passive" : "active"}`}>
            {params.row.status
              .toLowerCase()
              .replace(/_/g, " ")
              .replace(/[^a-zA-Z0-9 ]+(.)/g, (m: any, chr: any) => chr.toUpperCase())}
          </Box>
        ),
      },
      {
        field: "dietitianFullName",
        minWidth: 130,
        flex: 1,
        headerName: "Dietitian",
        valueGetter: (params: any) => params.row.dietitianFullName || "-",
      },
      {
        field: "driver.fullName",
        sortable: false,
        minWidth: 80,
        flex: 1,
        headerName: "Delivery Driver",
        valueGetter: (params: any) => params.row.user.driver?.fullName,
      },
      {
        field: "user.address.detail",
        sortable: false,
        minWidth: 80,
        flex: 2,
        headerName: "Delivery Address",
        renderCell: (params: GridRenderCellParams<any, any>) => (
          <Box display="flex" minHeight="45px" height="100%" alignItems="center">
            <Box display="flex" flexDirection="column">
              <Box>{toAddressString(params.row.user.address)}</Box>
              {params.row.user.address.detail ? (
                <Box sx={{ whiteSpace: "pre-line" }}>{params.row.user.address.detail}</Box>
              ) : null}
            </Box>
          </Box>
        ),
      },
      {
        field: "deliveryNote",
        minWidth: 80,
        flex: 2,
        headerName: "Delivery Note",
        valueGetter: (params: any) => params.row.deliveryNote,
      },
    ];
  }, [packages, selectedDate]);

  useEffect(() => {
    const getPackages = async () => {
      setLoading(true);
      try {
        const res = await Api.Delivery.listReviewsByDate(
          selectedDate.format(defaultFormat),
          filterModel.userExternalId,
          filterModel.userName,
          filterModel.status,
          filterModel.driverId,
          filterModel.dietitianId,
          paginationModel.page,
          paginationModel.pageSize,
          sortModel.map((i) => `${i.field},${i.sort}`)
        );
        var packages = res.data.content as DeliveryReviewResponse[];
        packages = packages.map((p, ind) => {
          var t = p as any;
          t.id = ind;
          return t;
        });
        setPackages(packages);
        setTotalRowCount(Number(res.data.totalElements));
      } catch (error) {}
      setLoading(false);
    };
    getPackages();
  }, [paginationModel, sortModel, selectedDate, filterModel, retriggerReq]);

  const pausedColumns = useMemo(() => {
    return [
      { field: "externalId", minWidth: 110, flex: 0, headerName: "Member ID" },
      {
        field: "fullName",
        minWidth: 120,
        flex: 2,
        headerName: "Name",
        renderCell: (params: any) => getUserNameWithIcon(params.row.fullName, params.row.channel),
      },
      { field: "phoneNumber", minWidth: 130, flex: 0, headerName: "Phone" },
      {
        field: "planName,planOptionSelection",
        minWidth: 200,
        sortable: true,
        flex: 0,
        headerName: "Subscription",
        renderCell: (params: any) => (
          <Box>
            <Box fontWeight={600}>{params.row.planName + " - " + params.row.planOptionSelection}</Box>
            <Box fontSize={10}>
              {params.row.planOptionMealTypes?.split(",")?.map((i: string) => mealTypeList.find((li) => li.name === i)?.description)?.join(", ") ?? ""}
            </Box>
          </Box>
        )
      },
      {
        field: "status",
        minWidth: 180,
        flex: 1.5,
        headerName: "Status",
        renderCell: (params: any) => (
          <Box className="status-label passive">
            Paused
          </Box>
        ),
      },
      { field: "dietitianFullName", minWidth: 130, flex: 2, headerName: "Dietitian" },
      {
        field: "subscriptionStartDate",
        minWidth: 120,
        flex: 1.5,
        headerName: "Subscription Start",
        valueGetter: (params: any) => params.row.subscriptionStartDate ? moment(params.row.subscriptionStartDate).format(displayFormat) : "-",
      },
      {
        field: "subscriptionEndDate",
        minWidth: 120,
        flex: 1.5,
        headerName: "Subscription End",
        valueGetter: (params: any) =>
          params.row.subscriptionEndDate
            ? moment(params.row.subscriptionEndDate).format(displayFormat)
              : "Paused"
      },
      {
        field: "pauseStartDate",
        minWidth: 120,
        flex: 1,
        headerName: "Pause Start",
        valueGetter: (params: any) => params.row.pauseStartDate ? moment(params.row.pauseStartDate).format(displayFormat) : "-",
      },
      {
        field: "pauseEndDate",
        minWidth: 120,
        flex: 1,
        headerName: "Pause End",
        valueGetter: (params: any) => params.row.pauseEndDate ? moment(params.row.pauseEndDate).format(displayFormat) : "-",
      },
    ];
  }, [pausedSubscriptions]);

  useEffect(() => {
    if (activeTab !== 1) return;
    const getPausedSubscriptions = async () => {
      setPausedLoading(true);
      try {
        const res = await Api.Subscription.listPausedSubscriptions(
          selectedDate.format(defaultFormat),
          pausedPaginationModel.page,
          pausedPaginationModel.pageSize,
          pausedSortModel.map((i) => `${i.field},${i.sort}`)
        );
        var items = (res.data.content as PausedSubscriptionResponse[]) || [];
        items = items.map((p, ind) => {
          var t = p as any;
          t.id = ind;
          return t;
        });
        setPausedSubscriptions(items);
        setPausedTotalRowCount(Number(res.data.totalElements));
      } catch (error) {}
      setPausedLoading(false);
    };
    getPausedSubscriptions();
  }, [activeTab, pausedPaginationModel, pausedSortModel, selectedDate]);

  const updateSelectedDate = (date: moment.Moment) => {
    setSearchParams((searchParams) => {
      searchParams.set("date", date.format("yyyy-MM-DD"));

      return searchParams;
    });
    setSelectedDate(date);
  };

  const editUsersMenuPlan = (params: any) => {
    setReviewDialog({ open: true, userId: params.row.user.id, date: selectedDate.format("yyyy-MM-DD") });
  };

  const editPausedUsersMenuPlan = (params: any) => {
    setReviewDialog({ open: true, userId: params.row.userId, date: selectedDate.format("yyyy-MM-DD") });
  };

  return (
    <>
      <Box className="delivery-report">
        <Box className="delivery-report-upper">
          <Box className="delivery-report-upper-left">
            <Box className="view-wrapper">
              <Button onClick={() => updateActiveTab(0)} className={`${activeTab === 0 ? "active" : ""}`}>
                Review
              </Button>
              <Button onClick={() => updateActiveTab(1)} className={`${activeTab === 1 ? "active" : ""}`}>
                Paused
              </Button>
            </Box>
          </Box>
          <Box className="delivery-report-upper-right">
            <Box className="delivery-report-selection-wrapper">
              <Box sx={{ width: 250 }}>
                <FormControl fullWidth>
                  <DatePicker
                    className="delivery-report-date-selector"
                    sx={{ "& .MuiOutlinedInput-root": { height: "44px" }, "& fieldset": { border: "none" } }}
                    value={selectedDate}
                    onChange={(date) => updateSelectedDate(moment(date))}
                    format="DD/MM/YYYY"
                  />
                </FormControl>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box p={6} className="delivery-report-container">
          {activeTab === 0 && (
            <DataTable
              rows={packages}
              rowCount={totalRowCount}
              columns={columns}
              sortingMode="server"
              sortModel={sortModel}
              paginationMode="server"
              paginationModel={paginationModel}
              paginationChange={handlePaginationChange}
              sortChange={handleSortChange}
              filterItems={filterItems}
              filterModel={filterModel}
              filterModelChange={setFilterModel}
              loading={loading}
              edit={editUsersMenuPlan}
              getRowHeight={() => "auto"}
              options={{
                hideActions: false,
                entityName: "package",
                filter: true,
                permission: { edit: "REVIEW_CREATE" },
              }}
            />
          )}

          {activeTab === 1 && (
            <DataTable
              rows={pausedSubscriptions}
              rowCount={pausedTotalRowCount}
              columns={pausedColumns}
              sortingMode="server"
              sortModel={pausedSortModel}
              paginationMode="server"
              paginationModel={pausedPaginationModel}
              paginationChange={handlePausedPaginationChange}
              sortChange={handlePausedSortChange}
              loading={pausedLoading}
              edit={editPausedUsersMenuPlan}
              options={{
                hideActions: false,
                hideToolbar: true,
                filter: false,
                entityName: "package",
                permission: { edit: "REVIEW_CREATE" },
              }}
            />
          )}
        </Box>
      </Box>
      <ReviewDialog
        open={reviewDialog.open}
        onClose={() => {
          setReviewDialog({ open: false, userId: "", date: "" });
          setRetriggerReq(generateRandom());
        }}
        onConfirm={() => {}}
        title="Review"
        userId={reviewDialog.userId}
        date={reviewDialog.date}
      />
    </>
  );
}
